// Central SEO / AEO / GEO configuration for Infini10 Technologies
// Used by <Seo /> component (react-helmet-async) to render per-page meta tags & JSON-LD

export const SITE_URL = "https://infini10technologies.com";
export const SITE_NAME = "Infini10 Technologies";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og/fallback.png`;
export const TWITTER_HANDLE = "@infini10tech";

// Reusable keyword clusters — India-primary + Global secondary
const KW_BRAND = ["Infini10", "Infini10 Technologies", "VerifyPro", "OpsDesk"];
const KW_BGV_INDIA = [
  "background verification India",
  "BGV software India",
  "employee verification platform India",
  "background check software India",
  "DPDP compliant BGV",
  "Aadhaar eKYC verification",
  "PAN verification API",
  "DigiLocker verification",
];
const KW_BGV_GLOBAL = [
  "BGV SaaS",
  "background verification platform",
  "employee background check software",
  "pre-employment screening software",
  "BFSI onboarding platform",
  "HRMS integration BGV",
];

// -------------------------- JSON-LD BUILDERS --------------------------
export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  legalName: "Infini10 Technologies Pvt. Ltd.",
  alternateName: "Infini10",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.svg`,
  image: DEFAULT_OG_IMAGE,
  description:
    "Infini10 Technologies is a Bangalore-headquartered SaaS product company. We build self-serve business software — starting with VerifyPro, an AI-powered Background Verification platform for Indian enterprises and BFSI, DPDP Act 2023 compliant by design.",
  foundingDate: "2014",
  areaServed: ["IN", "AE", "SG", "US", "GB"],
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "38, 1st Floor, Maruthi Nilaya, 1st Cross, 1st Main, MutturayaSwamy Layout, Hulimavu, Begur",
    addressLocality: "Bangalore",
    addressRegion: "Karnataka",
    postalCode: "560076",
    addressCountry: "IN",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+91-91102-48636",
      contactType: "sales",
      email: "admin@infini10technologies.com",
      areaServed: ["IN", "Global"],
      availableLanguage: ["English", "Hindi"],
    },
  ],
  sameAs: [],
  knowsAbout: [
    "Background Verification",
    "Employee Screening",
    "BGV SaaS",
    "IT Service Management",
    "DPDP Act 2023 compliance",
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  description:
    "Modern SaaS products from Infini10 Technologies — VerifyPro (background verification) and OpsDesk (IT service desk).",
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en-IN",
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const verifyProProductSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": `${SITE_URL}/services#verifypro`,
  name: "VerifyPro",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Background Verification Software",
  operatingSystem: "Web-based",
  url: "https://verifypro.infini10technologies.com/",
  brand: { "@type": "Brand", name: SITE_NAME },
  description:
    "VerifyPro is an AI-powered, multi-tenant Background Verification SaaS platform built for Indian enterprises and BFSI. Features candidate self check-in, Aadhaar eKYC, PAN & DigiLocker verification, live field-agent tracking, an AI CXO dashboard and a multimodal voice + gesture executive agent. DPDP Act 2023 compliant by design.",
  featureList: [
    "Multimodal Executive Agent (voice + hand-gesture)",
    "AI-powered CXO dashboards & case-topology map",
    "Live field-agent tracking on Google Maps",
    "Candidate self check-in with Aadhaar eKYC, PAN, DigiLocker",
    "HRMS connectors (Zoho People, Zoho Recruit, Keka, DarwinBox)",
    "Multi-tenant SaaS with masked storage & role-based access",
    "DPDP Act 2023 compliant by design",
  ],
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "INR",
    description: "14-day free trial. No credit card required. 10 free cases.",
    availability: "https://schema.org/InStock",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "42",
    bestRating: "5",
    worstRating: "1",
  },
});

export const opsDeskProductSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": `${SITE_URL}/services#opsdesk`,
  name: "OpsDesk",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "IT Service Management",
  operatingSystem: "Web-based",
  brand: { "@type": "Brand", name: SITE_NAME },
  description:
    "OpsDesk (coming soon) is a modern IT service-desk & ITSM platform for lean IT teams — tickets, incidents, SLAs and change management without the enterprise-suite bloat.",
});

// --- FAQ content (also used for on-page rendering) ---
export const VERIFYPRO_FAQS = [
  {
    q: "What is VerifyPro?",
    a: "VerifyPro is a self-serve, AI-powered Background Verification (BGV) SaaS platform built by Infini10 Technologies. It handles the full verification workflow — candidate self check-in, Aadhaar eKYC, PAN & DigiLocker verification, HRMS integration, live field-agent tracking and AI-powered CXO dashboards — from a single console. It is DPDP Act 2023 compliant by design and used by Indian enterprises and BFSI organisations.",
  },
  {
    q: "How does background verification work with VerifyPro?",
    a: "There are six steps: (1) Initiate a case via the portal or REST API. (2) The candidate completes a mobile-first consent flow. (3) Verifications run in parallel — identity, address, education, employment, criminal court, database, drug, credit and reference checks. (4) Every discrepancy is graded Major / Moderate / Minor / Insufficient with documentary evidence. (5) A tamper-evident PDF report is delivered with full audit trail. (6) Optional annual re-verification and watchlist monitoring can be enabled for sensitive roles.",
  },
  {
    q: "What is the turnaround time (TAT) for a background check?",
    a: "The median TAT is 5 working days for the standard pack, and as fast as 48 hours for fast-track identity + database checks. Real-time SLA monitoring is available in the dashboard, and CXO users are auto-alerted when a case stalls past its SLA.",
  },
  {
    q: "Which HRMS and ATS platforms does VerifyPro integrate with?",
    a: "VerifyPro ships with native connectors for Zoho People, Zoho Recruit, Keka and DarwinBox. Cases are auto-triggered when a new hire is added, and status, risk scores and report links are pushed back into the employee record. Additional platforms are supported via REST API, webhooks, SSO and bulk CSV.",
  },
  {
    q: "Is VerifyPro compliant with India's DPDP Act 2023?",
    a: "Yes. VerifyPro is DPDP Act 2023 compliant by design. Personal data is masked at rest, stored in India, and access is role-based with a full audit trail. Consent capture is built into the candidate flow, and data retention & deletion policies are configurable per tenant.",
  },
  {
    q: "What checks are included in a VerifyPro report?",
    a: "VerifyPro supports over 40 check types across nine categories: Identity (PAN, Aadhaar, Passport, Voter ID, DL), Address (current, permanent, cohabitant, up to 7-year history), Education (degree, marksheet, direct institute, WES), Employment (past employer, tenure, salary, EPFO/UAN), Criminal & Court (civil, criminal, district-level), Reference (structured 360°), Database & Watchlist (OFAC, UN, EU, PEP), Drug & Health (5-panel, 10-panel via NABL labs) and Credit & Financial (CIBIL, Experian, DIN).",
  },
  {
    q: "What is the Multimodal Executive Agent in VerifyPro?",
    a: "The Multimodal Executive Agent is a voice- and hand-gesture-controlled virtual assistant available inside the VerifyPro CXO and HR consoles. Executives can wake it with a gesture and ask for insights out loud — like 'show me discrepancies this week' — and get instant natural-language answers on cases, TAT and financial exposure. It is opt-in, privacy-first, and no recordings are stored.",
  },
  {
    q: "How much does VerifyPro cost?",
    a: "VerifyPro offers three tiers: Starter (₹0 for a 14-day trial, up to 10 cases), Professional (₹6,999 per month for up to 250 cases, all 40+ check types, REST API and SSO), and Enterprise (custom pricing for unlimited cases, data residency, dedicated success manager and white-labelled candidate flow).",
  },
];

export const faqSchema = (faqs = VERIFYPRO_FAQS) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const howToSchema = () => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to run a background verification check with VerifyPro",
  description:
    "Step-by-step guide to running an audit-grade background verification (BGV) case using Infini10 VerifyPro.",
  totalTime: "PT5D",
  supply: [{ "@type": "HowToSupply", name: "Candidate PAN / Aadhaar" }],
  tool: [{ "@type": "HowToTool", name: "VerifyPro dashboard or REST API" }],
  step: [
    { "@type": "HowToStep", position: 1, name: "Initiate", text: "Raise a case in the VerifyPro portal or via REST API. Bulk uploads and white-label flows are supported." },
    { "@type": "HowToStep", position: 2, name: "Consent", text: "The candidate receives a mobile-first branded consent link and uploads PII documents." },
    { "@type": "HowToStep", position: 3, name: "Verify", text: "VerifyPro runs identity, address, education, employment, criminal, database, drug and reference checks in parallel." },
    { "@type": "HowToStep", position: 4, name: "Adjudicate", text: "Every discrepancy is graded Major, Moderate, Minor or Insufficient with documentary evidence." },
    { "@type": "HowToStep", position: 5, name: "Report", text: "A tamper-evident PDF report is delivered with full audit trail and executive summary." },
    { "@type": "HowToStep", position: 6, name: "Re-verify", text: "Optional annual re-verification and watchlist monitoring can be enabled." },
  ],
});

// -------------------------- PER-PAGE SEO --------------------------
export const PAGE_SEO = {
  home: {
    path: "/",
    image: `${SITE_URL}/og/home.png`,
    title: "Infini10 Technologies | VerifyPro Background Verification SaaS · Bangalore",
    description:
      "Infini10 Technologies builds modern self-serve SaaS. Meet VerifyPro — India's AI-powered Background Verification platform for enterprises and BFSI. DPDP Act 2023 compliant. 14-day free trial, no credit card.",
    keywords: [...KW_BRAND, ...KW_BGV_INDIA, ...KW_BGV_GLOBAL].slice(0, 20),
    h1Fact:
      "Infini10 Technologies is a Bangalore-based SaaS product company. Our flagship product, VerifyPro, is an AI-powered background verification platform used by Indian enterprises and BFSI. DPDP Act 2023 compliant. Founded 2014.",
    breadcrumbs: [{ name: "Home", path: "/" }],
  },
  about: {
    path: "/about",
    image: `${SITE_URL}/og/about.png`,
    title: "About Infini10 Technologies | Bangalore SaaS Product Company",
    description:
      "Infini10 Technologies is a Bangalore-headquartered SaaS product company founded in 2014. We build opinionated, self-serve business software — starting with VerifyPro, our flagship background-verification platform.",
    keywords: [
      ...KW_BRAND,
      "Infini10 Technologies Bangalore",
      "SaaS company India",
      "BGV company India",
      "background verification company Bangalore",
    ],
    h1Fact:
      "Infini10 Technologies is a Bangalore SaaS product company founded in 2014. We build one login, one billing account and one design language — starting with VerifyPro (live) and OpsDesk (coming soon).",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
    ],
  },
  products: {
    path: "/services",
    image: `${SITE_URL}/og/products.png`,
    title: "VerifyPro & OpsDesk | Infini10 Products · Background Verification SaaS",
    description:
      "Explore Infini10 products: VerifyPro — AI-powered background verification (live, DPDP compliant, HRMS-integrated) and OpsDesk — modern IT service desk (coming soon). Multimodal executive agent, live field-agent tracking, Aadhaar eKYC, DigiLocker.",
    keywords: [
      ...KW_BRAND,
      ...KW_BGV_INDIA,
      ...KW_BGV_GLOBAL,
      "VerifyPro pricing",
      "VerifyPro integrations",
      "VerifyPro Aadhaar eKYC",
      "VerifyPro Zoho integration",
      "VerifyPro Keka integration",
      "VerifyPro DarwinBox integration",
      "IT service desk software India",
    ],
    h1Fact:
      "Infini10 ships two SaaS products: VerifyPro (live) — background verification with Aadhaar eKYC, PAN, DigiLocker, multimodal executive agent, live field-agent tracking on Google Maps and native HRMS connectors for Zoho People, Zoho Recruit, Keka and DarwinBox. OpsDesk (coming soon) — ITSM for lean IT teams.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Products", path: "/services" },
    ],
  },
  contact: {
    path: "/contact",
    image: `${SITE_URL}/og/contact.png`,
    title: "Contact Infini10 Technologies | Book a VerifyPro Demo · Bangalore",
    description:
      "Talk to Infini10 Technologies about VerifyPro, OpsDesk or managed BGV operations. Bangalore HQ · reply within 1 business day. Email admin@infini10technologies.com or call +91 91102 48636.",
    keywords: [
      ...KW_BRAND,
      "contact Infini10",
      "VerifyPro demo",
      "book BGV demo India",
      "background verification demo",
    ],
    h1Fact:
      "Contact Infini10 Technologies for a VerifyPro demo, managed BGV operations, or partnership enquiries. Bangalore HQ · Mon–Sat 9:30 AM – 7:00 PM IST · reply within 1 business day.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Contact", path: "/contact" },
    ],
  },
};
